import React, { useState } from 'react'

import { getErrorMessage } from 'utils'

import type { Product } from 'types'

import { useSnackbar } from 'contexts'
import { useProductMutations } from 'hooks/api'

import { Stack, Typography, Divider } from '@mui/material'
import { CircleAlertIcon } from 'lucide-react'

import { Modal } from '@repo/ui'
import { Button } from '@repo/ui'
import { Alert } from '@repo/ui'
import { ProductPreview } from 'components'

interface ProductWithCertificatesCount extends Product {
  certificates_count: number
}

interface BulkDeleteProductsModalProps {
  products: ProductWithCertificatesCount[]
  open: boolean
  onClose: () => void
  onConfirm: () => void
  isLoading: boolean
}

export const BulkDeleteProductsModal = (props: BulkDeleteProductsModalProps) => {
  const { products, open, onClose, onConfirm, isLoading } = props

  const { open: notification } = useSnackbar()

  // Validate that no products have certificates
  const productsWithCertificates = products.filter(p => p.certificates_count > 0)
  const canDelete = productsWithCertificates.length === 0

  const handleClose = () => {
    onClose()
  }

  const handleConfirm = () => {
    if (!canDelete) {
      notification({
        type: 'error',
        title: `${productsWithCertificates.length} selected product(s) have linked certificates and cannot be deleted`,
      })
      return
    }
    onConfirm()
  }

  return (
    <Modal
      title={`Delete ${products.length} Product${products.length > 1 ? 's' : ''}`}
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button
            disableElevation
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>

          <Button
            disableElevation
            destructive
            onClick={handleConfirm}
            disabled={!canDelete || isLoading}
          >
            {isLoading ? 'Deleting...' : `Delete ${products.length} Product${products.length > 1 ? 's' : ''}`}
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        <Typography variant="body2" color="text.secondary">
          Are you sure you want to delete the following {products.length} product{products.length > 1 ? 's' : ''}?
        </Typography>

        {productsWithCertificates.length > 0 && (
          <Alert
            severity="error"
            icon={<CircleAlertIcon strokeWidth={1.5} size={20} />}
            title={`${productsWithCertificates.length} product${productsWithCertificates.length > 1 ? 's' : ''} cannot be deleted:`}
          >
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              {productsWithCertificates.slice(0, 3).map((product) => (
                <Typography key={product.id} variant="body3" sx={{ color: 'inherit' }}>
                  • {product.title} (has {product.certificates_count} certificate{product.certificates_count > 1 ? 's' : ''})
                </Typography>
              ))}
              {productsWithCertificates.length > 3 && (
                <Typography variant="body3" sx={{ color: 'inherit' }}>
                  • ... and {productsWithCertificates.length - 3} more
                </Typography>
              )}
            </Stack>
          </Alert>
        )}

        <Stack
          spacing={2}
          sx={{
            maxHeight: '300px',
            overflowY: 'auto',
            pr: 1,
          }}
        >
          {products.map((product) => (
            <ProductPreview key={product.id} product={product} size="medium" />
          ))}
        </Stack>
      </Stack>
    </Modal>
  )
}

